import { USD_PER_CREDIT } from '@/lib/billing/credit-economics'
import { BILLING_PLANS } from '@/lib/billing/plans'
import { defaultPriceSummary } from '@/lib/billing/price-summary'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'
import { LEGAL_LINKS } from '@/lib/site/legal-links'

/**
 * The /enterprise page: one Enterprise plan the reader builds. The builder,
 * the page's Markdown twin (`/enterprise.md`) and its OG card all read these
 * numbers, so they live in `lib/` where the SEO modules may import them.
 *
 * A platform fee covers dedicated support, team controls, unlimited brands
 * and data privacy; the reader picks AI credits and
 * emails from tiers that start above Pro and cost less per unit the more
 * they take, then the optional add-ons: migration support and managed
 * lifecycle. Every line says what it
 * costs, and the total updates as they choose.
 *
 * The prices are the founders' working numbers; change them here and the
 * builder and its total follow.
 */

export type VolumeOption = Readonly<{
  /** Units a month; `null` for "more than this", priced on a call. */
  amount: number | null
  /** Dollars a month for this tier; `null` when priced on a call. */
  addUsd: number | null
}>

export type EnterpriseAddOn = Readonly<{
  id: 'migration' | 'lifecycle'
  name: string
  description: string
  priceUsd: number
}>

/**
 * The platform fee: what every Enterprise plan pays for dedicated support,
 * team controls, unlimited brands and data privacy, before any credits or
 * emails. Some of it is on self-serve plans too; it is listed because
 * procurement checks for it. Each line is something we have today.
 * `credits` and `sends` are the smallest tiers a plan can pick.
 */
export type IncludedItem = Readonly<{
  id: 'support' | 'privacy' | 'team' | 'brands'
  title: string
  description: string
  /** Documents a reviewer can open, shown under the line. */
  links?: ReadonlyArray<Readonly<{ label: string; href: string }>>
}>

export const ENTERPRISE_BASE: Readonly<{
  name: string
  priceUsd: number
  credits: number
  sends: number
  included: ReadonlyArray<IncludedItem>
}> = {
  name: 'Platform fee',
  priceUsd: 499,
  credits: 25_000,
  sends: 250_000,
  included: [
    {
      id: 'support',
      title: 'Dedicated support',
      description:
        'A named account manager and a shared Slack channel with answers within 48 hours. Response times and an uptime target are written into your contract.',
    },
    {
      id: 'team',
      title: 'Team controls',
      description:
        'Choose which brands each teammate can reach, approve emails from Slack, and give developers API and MCP keys scoped to one brand.',
    },
    {
      id: 'brands',
      title: 'Unlimited brands',
      description:
        'As many brands, sending domains and team members as you need.',
    },
    {
      id: 'privacy',
      title: 'Data privacy',
      description:
        'A signed DPA, deletion on request, and your data is never used to train AI models.',
      links: LEGAL_LINKS,
    },
  ],
}

/** AI credits a month. Every plan picks one; the first is the smallest. */
export const CREDIT_OPTIONS: ReadonlyArray<VolumeOption> = [
  { amount: 25_000, addUsd: 230 },
  { amount: 50_000, addUsd: 455 },
  { amount: 100_000, addUsd: 875 },
]

/** Emails a month. Every plan picks one; the first is the smallest. */
export const SEND_OPTIONS: ReadonlyArray<VolumeOption> = [
  { amount: 250_000, addUsd: 270 },
  { amount: 500_000, addUsd: 530 },
  { amount: 1_000_000, addUsd: 1025 },
  { amount: 2_500_000, addUsd: 2425 },
  { amount: 5_000_000, addUsd: 4730 },
  { amount: 10_000_000, addUsd: 9300 },
  { amount: null, addUsd: null },
]

/**
 * What a unit costs us. A credit is $0.01 of model spend by definition
 * (`USD_PER_CREDIT`). An email costs $0.20 per 1,000 on Mailgun, where
 * sending is moving (Sinch service order #00005098, Aug 2026 to Aug 2027),
 * and about $0.65 per 1,000 on Resend's transactional list rate, the
 * dearer of our two senders, with no volume discount.
 */
export const UNIT_COST_USD = {
  credit: USD_PER_CREDIT,
  email: { mailgun: 0.2 / 1000, resend: 0.65 / 1000 },
} as const

/**
 * Credits a new email uses, to say what a credit amount gets you. An email
 * costs what the model actually used (`lib/billing/operation-credits.ts`
 * meters `email.generate` by usage), so this is the founders' planning rate:
 * about 100 credits a new email, with real ones between ~33 and ~167.
 * Edits are metered too, so a long email can cost more; audits (5), previews
 * (10) and images use few. Importing HTML, MJML, React Email or Figma and
 * exporting are free; turning a screenshot into an email is not.
 */
export const CREDITS_PER_NEW_EMAIL = 100

/** "Enough to design about 250 new emails a month". */
export function creditsInEmails(credits: number): string {
  return `Enough to design about ${emailsFromCredits(credits)}`
}

/** "250": roughly how many new emails a credit amount designs. */
export function emailsDesigned(credits: number): string {
  return Math.round(credits / CREDITS_PER_NEW_EMAIL).toLocaleString('en-US')
}

/** "250 new emails a month": the part of the line the page sets in bold. */
export function emailsFromCredits(credits: number): string {
  return `${emailsDesigned(credits)} new emails a month`
}

/** The least gross margin a line may earn at full use, at list cost. */
export const MIN_GROSS_MARGIN = 0.3

/** Dollars from a price summary label ("$249" → 249). */
function labelUsd(label: string | undefined, fallback: number): number {
  const value = Number(label?.replace(/[^0-9.]/g, ''))
  return Number.isFinite(value) && value > 0 ? value : fallback
}

/**
 * What one credit and one email cost on Pro, the largest self-serve plan:
 * its monthly price over what it includes. The badges compare each
 * Enterprise step against buying the same amount at this rate.
 */
export function selfServeUnitRate(): Readonly<{
  credit: number
  email: number
}> {
  const pro = labelUsd(defaultPriceSummary().pro?.label, 249)
  return {
    credit: pro / BILLING_PLANS.pro.credits,
    email: pro / BILLING_PLANS.pro.emailSends,
  }
}

/**
 * What each email over the chosen tier costs, in dollars: Pro's rate, so a
 * tier is always cheaper than going over it. Enterprise orgs are not gated at
 * their tier, and nothing meters them per month yet (the `send_quota_overage`
 * alert skips plans with no included sends), so the founders count the extra
 * from `billingUsageEvents` and invoice it with the next month.
 */
export function emailOverageUsd(): number {
  return Math.round(selfServeUnitRate().email * 100_000) / 100_000
}

/** "$0.00125 each ($1.25 per 1,000)". */
export function formatEmailOverage(): string {
  const perEmail = emailOverageUsd()
  return `$${perEmail} each (${formatUsd(Math.round(perEmail * 1000 * 100) / 100)} per 1,000)`
}

/**
 * What a tier saves against the same volume at Pro's rate, as a whole
 * percent ("Save 12%"). `null` for the open-ended tier.
 */
export function volumeSavingsPercent(
  options: ReadonlyArray<VolumeOption>,
  index: number,
  selfServeRate: number
): number | null {
  const option = options[index]
  if (!option || option.amount === null || option.addUsd === null) {
    return null
  }
  const percent = Math.round(
    (1 - option.addUsd / (selfServeRate * option.amount)) * 100
  )
  return percent > 0 ? percent : null
}

export const ENTERPRISE_ADD_ONS: ReadonlyArray<EnterpriseAddOn> = [
  {
    id: 'migration',
    name: 'Migration support',
    description:
      'We help you move your templates, contacts, unsubscribes, automations and domains from your current platform, and keep helping as you move more over.',
    priceUsd: 999,
  },
  {
    id: 'lifecycle',
    name: 'Managed lifecycle',
    description:
      'We run your email program for you. Ask for a new email on any weekday and it is ready within 48 hours.',
    priceUsd: 2999,
  },
]

export type EnterpriseSelection = Readonly<{
  creditIndex: number
  sendIndex: number
  addOns: ReadonlyArray<EnterpriseAddOn['id']>
}>

export type EnterpriseQuote = Readonly<{
  /** `null` when a choice is priced on a call. */
  monthlyUsd: number | null
  lines: ReadonlyArray<Readonly<{ label: string; usd: number | null }>>
}>

export function formatUsd(amount: number): string {
  return `$${amount.toLocaleString('en-US')}`
}

/** 1,000,000 → "1M", 250,000 → "250K". */
function formatVolume(amount: number): string {
  if (amount >= 1_000_000) {
    return `${amount / 1_000_000}M`
  }
  return `${amount / 1000}K`
}

/** An option's label; the open-ended last option reads "1M+". */
export function volumeLabel(
  options: ReadonlyArray<VolumeOption>,
  index: number
): string {
  const option = options[index]
  if (!option) {
    return ''
  }
  if (option.amount === null) {
    return `${formatVolume(options.at(-2)?.amount ?? 0)}+`
  }
  return formatVolume(option.amount)
}

/** The email and credit tiers picked, the add-ons, then the platform fee. */
export function getEnterpriseQuote(
  selection: EnterpriseSelection
): EnterpriseQuote {
  const credit = CREDIT_OPTIONS[selection.creditIndex]
  const send = SEND_OPTIONS[selection.sendIndex]
  const lines: Array<{ label: string; usd: number | null }> = []
  if (send) {
    lines.push({
      label: `${volumeLabel(SEND_OPTIONS, selection.sendIndex)} emails`,
      usd: send.addUsd,
    })
  }
  if (credit) {
    lines.push({
      label: `${volumeLabel(CREDIT_OPTIONS, selection.creditIndex)} AI credits`,
      usd: credit.addUsd,
    })
  }
  for (const addOn of ENTERPRISE_ADD_ONS) {
    if (selection.addOns.includes(addOn.id)) {
      lines.push({ label: addOn.name, usd: addOn.priceUsd })
    }
  }
  // Last, as on the page: the choices first, then the fee every plan pays.
  lines.push({ label: ENTERPRISE_BASE.name, usd: ENTERPRISE_BASE.priceUsd })
  const monthlyUsd = lines.some((line) => line.usd === null)
    ? null
    : lines.reduce((sum, line) => sum + (line.usd ?? 0), 0)
  return { monthlyUsd, lines }
}

/** The least an Enterprise plan costs: the fee and the smallest tiers. */
export const ENTERPRISE_STARTING_USD =
  ENTERPRISE_BASE.priceUsd +
  (CREDIT_OPTIONS[0]?.addUsd ?? 0) +
  (SEND_OPTIONS[0]?.addUsd ?? 0)

export const ENTERPRISE_CTA_LABEL = 'Book a call'
export const ENTERPRISE_CTA_URL = BREW_DEMO_BOOKING_URL
